/**
 * ADVENTURE MAP SYSTEM - Phase 2 Gamification
 * Visual progress tracking through an island-hopping adventure
 */

const AdventureMap = {
    // Map configuration
    config: {
        currentIsland: 1,
        maxIslands: 10, // Matches 10 levels in xpSystem
        unlockedIslands: 1,
        problemsPerIsland: 5,
        visitedProblems: 0
    },

    // Island themes (one per level)
    islands: [
        { name: 'Isla del Aprendiz', icon: '🏝️', color: '#FFE5B4', description: 'Tu aventura comienza aquí' },
        { name: 'Bosque de los Números', icon: '🌳', color: '#C8E6C9', description: 'Explora los secretos matemáticos' },
        { name: 'Montaña del Razonamiento', icon: '⛰️', color: '#B0BEC5', description: 'Escala nuevas alturas' },
        { name: 'Cueva de las Operaciones', icon: '🗻', color: '#BCAAA4', description: 'Descubre tesoros escondidos' },
        { name: 'Lago de la Lógica', icon: '🏞️', color: '#81D4FA', description: 'Navega las aguas del pensamiento' },
        { name: 'Volcán de los Desafíos', icon: '🌋', color: '#FFAB91', description: 'Supera pruebas ardientes' },
        { name: 'Templo de la Sabiduría', icon: '🏛️', color: '#FFF59D', description: 'Alcanza la maestría' },
        { name: 'Jardín de las Estrategias', icon: '🌺', color: '#F8BBD0', description: 'Cultiva tu ingenio' },
        { name: 'Torre del Genio', icon: '🗼', color: '#CE93D8', description: 'Toca el cielo matemático' },
        { name: 'Palacio Sherlock', icon: '👑', color: '#FFD54F', description: 'Conviértete en leyenda' }
    ],

    // DOM elements
    mapContainer: null,
    islandElements: [],

    /**
     * Initialize the adventure map
     */
    init() {
        // Load progress from gamification service
        if (window.GamificationService) {
            const progress = window.GamificationService.getProgress();
            this.config.currentIsland = progress.level || 1;
            this.config.unlockedIslands = progress.level || 1;
            this.config.visitedProblems = progress.problemsCompleted || 0;
        }

        console.log('🗺️ Adventure Map initialized');
    },

    /**
     * Render the adventure map UI
     * @param {HTMLElement} container - Container element to render map into
     */
    render(container) {
        if (!container) {
            console.error('⚠️ No container provided for adventure map');
            return;
        }

        try {
            // Refrescar estado en tiempo real desde GamificationService
            if (window.GamificationService) {
                const progress = window.GamificationService.getProgress();
                this.config.unlockedIslands = progress.level || this.config.unlockedIslands;
                this.config.visitedProblems = progress.problemsCompleted || this.config.visitedProblems;
            }

            this.mapContainer = container;
            container.innerHTML = '';
            container.className = 'adventure-map';

            // Create map header
            const header = document.createElement('div');
            header.className = 'map-header';

            header.innerHTML = `
            <div style="width: 100%; margin-bottom: 25px; position: relative; display: flex; align-items: center; justify-content: center; min-height: 70px; background: rgba(255,255,255,0.1); border-radius: 12px; padding: 10px; border: 1px solid rgba(255,255,255,0.2);">
                <button class="btn btn-secondary" onclick="window.AdventureMap.close()" style="position: absolute; left: 15px; top: 50%; transform: translateY(-50%); z-index: 20; padding: 8px 16px; font-size: 0.9rem; height: auto; min-height: 0; white-space: nowrap; box-shadow: 0 4px 0 #334155; font-weight: 800;">
                   ← VOLVER
                </button>
                <div style="text-align: center; width: 100%; padding: 0 120px; pointer-events: none;">
                    <h1 style="margin:0; font-size: 1.6rem; color: #1e293b; line-height: 1.2; font-weight: 800; text-transform: uppercase; letter-spacing: 1px;">🗺️ Tu Aventura</h1>
                    <p class="map-subtitle" style="margin:5px 0 0 0; font-size: 0.9rem; color: #475569; font-weight: 600;">Completa problemas para desbloquear islas</p>
                </div>
            </div>
        `;
            container.appendChild(header);

            // Create islands grid
            const grid = document.createElement('div');
            grid.className = 'islands-grid';

            this.islands.forEach((island, index) => {
                const islandNum = index + 1;
                const isUnlocked = islandNum <= this.config.unlockedIslands;
                const isCurrent = islandNum === this.config.currentIsland;

                const islandCard = this.createIslandCard(island, islandNum, isUnlocked, isCurrent);
                this.islandElements[index] = islandCard;
                grid.appendChild(islandCard);

                // Add connecting path (except for last island)
                if (index < this.islands.length - 1) {
                    const path = document.createElement('div');
                    path.className = `island-path ${isUnlocked ? 'unlocked' : 'locked'}`;
                    path.innerHTML = isUnlocked ? '━━━━━━━━' : '┈┈┈┈┈┈┈┈';
                    grid.appendChild(path);
                }
            });

            container.appendChild(grid);

            // Add progress summary
            const summary = this.createProgressSummary();
            container.appendChild(summary);


            console.log('🗺️ Map rendered with', this.islands.length, 'islands');
        } catch (error) {
            console.error('❌ Error rendering Adventure Map:', error);
            container.innerHTML = `
                <div style="text-align: center; padding: 2rem; color: #721c24; background-color: #f8d7da; border: 1px solid #f5c6cb; border-radius: 8px;">
                    <h3>⚠️ Error al cargar el mapa</h3>
                    <p>Lo sentimos, ha ocurrido un problema visual.</p>
                    <p style="font-size: 0.8rem; opacity: 0.8;">${error.message}</p>
                    <button class="btn btn-secondary" onclick="window.AdventureMap.close()">Volver</button>
                </div>
            `;
        }
    },

    /**
     * Create an island card element
     */
    createIslandCard(island, number, isUnlocked, isCurrent) {
        const card = document.createElement('div');
        card.className = `island-card ${isUnlocked ? 'unlocked' : 'locked'} ${isCurrent ? 'current' : ''}`;
        card.style.borderColor = island.color;

        if (isUnlocked) {
            card.style.cursor = 'pointer';
            card.onclick = () => {
                // DEMO MODE RESTRICTION
                // Only allow specific islands/contexts
                // Island 1 = Frutería (usually) or generic start.
                // We need to check if this island maps to 'fruteria' or 'cumpleanos'.
                // Since this map uses generic names ("Isla del Aprendiz"), we might need to map by index
                // OR we check the island name if it matched context.
                // However, the AdventureMap usually just sets the visual level.
                // The actual context selection might happen elsewhere?
                // Let's check where `selectIsland` is defined or what it does.
                this.selectIsland(number);
            };
        }

        card.innerHTML = `
    <div class="island-icon" style="background: ${island.color}">
        ${island.icon}
            </div>
    <div class="island-info">
        <div class="island-number">Isla ${number}</div>
        <div class="island-name">${island.name}</div>
        ${isUnlocked ?
                `<div class="island-actions" style="margin-top:5px; display:flex; flex-direction:column; gap:8px;">
                <button class="btn-mini-play" onclick="event.stopPropagation(); window.CurricularGamification.showIslandReward(${number})" style="z-index:10; cursor:pointer; background: #3b82f6; color: white; border: none; padding: 6px 12px; border-radius: 8px; font-weight: bold; font-size: 0.8rem; box-shadow: 0 3px 0 #1d4ed8;">🎮 Jugar Reto</button>
                <div class="island-desc">${island.description}</div>
             </div>`
                : '<div class="island-locked">🔒 Bloqueada</div>'}
    </div>
            ${isCurrent ? '<div class="current-marker">📍 Estás aquí</div>' : ''}
`;

        return card;
    },

    /**
     * Create progress summary footer
     */
    createProgressSummary() {
        const summary = document.createElement('div');
        summary.className = 'map-summary';

        // Robust access to GamificationService
        const progress = (window.GamificationService && typeof window.GamificationService.getProgress === 'function')
            ? window.GamificationService.getProgress()
            : {};

        const totalProblems = progress.problemsCompleted || 0;
        const stars = progress.stars || 0;

        summary.innerHTML = `
    <div class="summary-stat">
                <span class="stat-icon">📚</span>
                <span class="stat-value">${totalProblems}</span>
                <span class="stat-label">Problemas</span>
            </div>
            <div class="summary-stat">
                <span class="stat-icon">⭐</span>
                <span class="stat-value">${stars}</span>
                <span class="stat-label">Estrellas</span>
            </div>
            <div class="summary-stat">
                <span class="stat-icon">🏝️</span>
                <span class="stat-value">${this.config.unlockedIslands}/${this.islands.length}</span>
                <span class="stat-label">Islas</span>
            </div>
`;

        return summary;
    },

    /**
     * Select/navigate to an island
     */
    selectIsland(islandNumber) {
        if (window.stopAudio) window.stopAudio();
        if (islandNumber > this.config.unlockedIslands) {
            console.log('🔒 Isla aún bloqueada');
            if (window.SoundService) window.SoundService.playError();
            return;
        }

        this.config.currentIsland = islandNumber;
        const island = this.islands[islandNumber - 1];
        console.log(`🏝️ Navegando a isla ${islandNumber}: ${island.name} `);

        // Update UI
        this.updateIslandStates();

        // Audio Feedback
        if (window.speak) {
            window.speak(`¡Viajando a: ${island.name} !`, 'es-ES');
        }

        // Play sound
        if (window.SoundService) window.SoundService.playSuccess();

        // Trigger event for other systems
        window.dispatchEvent(new CustomEvent('adventureMap:islandSelected', {
            detail: {
                islandNumber,
                island: this.islands[islandNumber - 1]
            }
        }));

        // Asignar el nivel correspondiente a la isla elegida en store.config
        if (typeof store !== 'undefined' && store.config) {
            const levelMap = { 1: 'aprendiz', 2: 'aprendiz', 3: 'avanzado', 4: 'avanzado', 5: 'experto' };
            store.config.level = levelMap[islandNumber] || (islandNumber > 5 ? 'experto' : 'aprendiz');
        }

        // Navigate after short delay to let audio finish
        setTimeout(() => {
            // Close map screen using robust method
            this.close();

            // Start configuration flow with updated level
            if (window.showConfigScreen) {
                window.showConfigScreen();
            } else if (window.showWelcomeScreen) {
                window.showWelcomeScreen();
            }
        }, 1500);
    },

    /**
     * Update island visual states
     */
    updateIslandStates() {
        this.islandElements.forEach((element, index) => {
            const islandNum = index + 1;
            const isCurrent = islandNum === this.config.currentIsland;

            element.classList.toggle('current', isCurrent);

            // Update current marker
            const marker = element.querySelector('.current-marker');
            if (marker) marker.remove();

            if (isCurrent) {
                const newMarker = document.createElement('div');
                newMarker.className = 'current-marker';
                newMarker.textContent = '📍 Estás aquí';
                element.appendChild(newMarker);
            }
        });
    },

    /**
     * Unlock next island (called when leveling up)
     */
    unlockNextIsland() {
        if (this.config.unlockedIslands < this.islands.length) {
            this.config.unlockedIslands++;
            this.config.currentIsland = this.config.unlockedIslands;

            console.log(`🎉 ¡Nueva isla desbloqueada! ${this.islands[this.config.unlockedIslands - 1].name} `);

            // Show celebration modal
            this.showIslandUnlockModal(this.config.unlockedIslands);

            // Re-render if map is visible
            if (this.mapContainer) {
                this.render(this.mapContainer);
            }

            return true;
        }
        return false;
    },

    /**
     * Show modal when new island is unlocked
     */
    showIslandUnlockModal(islandNumber) {
        const island = this.islands[islandNumber - 1];

        const modal = document.createElement('div');
        modal.className = 'island-unlock-modal';
        modal.innerHTML = `
    <div class="unlock-modal-content">
                <div class="unlock-icon">${island.icon}</div>
                <h2>🎊 ¡Nueva Isla Desbloqueada!</h2>
                <h3>${island.name}</h3>
                <p>${island.description}</p>
                <button class="btn btn-primary" onclick="this.closest('.island-unlock-modal').remove()">
                    ¡Explorar! 🚀
                </button>
            </div>
    `;

        document.body.appendChild(modal);

        // Play confetti
        if (window.confetti) {
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 }
            });
        }

        // Auto-close after 5 seconds
        setTimeout(() => {
            if (modal.parentNode) {
                modal.remove();
            }
        }, 5000);
    },

    /**
     * Get current island info
     */
    getCurrentIsland() {
        return {
            number: this.config.currentIsland,
            ...this.islands[this.config.currentIsland - 1]
        };
    },
    /**
     * Close map and return to main menu
     */
    close() {
        if (window.stopAudio) window.stopAudio();
        console.log('🗺️ Cerrando mapa de aventura...');

        // Priority: Use SessionManager if available
        if (window.SessionManager && typeof window.SessionManager.goBackGlobal === 'function') {
            window.SessionManager.goBackGlobal();
        } else {
            // Fallback: Manually switch screens
            const mapScreen = document.getElementById('mapScreen');
            if (mapScreen) {
                mapScreen.classList.add('hidden');
                mapScreen.classList.remove('active');
            }

            // Try different welcome screen showing methods
            if (window.SessionManager && typeof window.SessionManager.showWelcomeScreen === 'function') {
                window.SessionManager.showWelcomeScreen();
            } else if (typeof window.showWelcomeScreen === 'function') {
                window.showWelcomeScreen();
            } else {
                const welcomeScreen = document.getElementById('welcomeScreen');
                if (welcomeScreen) {
                    welcomeScreen.classList.remove('hidden');
                    welcomeScreen.classList.add('active');
                    // Ensure UI is rendered
                    if (window.UIManager && typeof window.UIManager.renderWelcomeScreen === 'function') {
                        window.UIManager.renderWelcomeScreen();
                    }
                }
            }
        }
    }
};

// Expose globally
window.AdventureMap = AdventureMap;

// Initialize on load
// Auto-init removed. Managed by Login Screen.
// if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', () => window.AdventureMap.init());
// } else {
//     window.AdventureMap.init();
// }

console.log('🗺️ adventureMap.js cargado');
